import React from 'react';
import ProgressIcon from '../../../assets/images/progressIcon.svg';
import DefaultProfile from '../../../assets/images/profile.png';
import { TimeTrackingData } from '../../../types/task';
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

interface EmployeeTimeTrackingProps {
  data?: TimeTrackingData;
  isLoading?: boolean;
}

const EmployeeTaskTracker: React.FC<EmployeeTimeTrackingProps> = ({
  data,
  isLoading,
}) => {
  const user = data?.user;
  const dailyData = data?.dailyData || [];
  const workedHours = data?.workedHours || 0;
  const totalHours = data?.totalHours || 9;
  const hrmsPunchingData = data?.hrmsPunchingData;

  const localTime = new Date().toLocaleString();

  const chartData = dailyData.map((item) => ({
    day: item.day,
    value: item.value,
  }));

  const ResponsiveContainerComponent = ResponsiveContainer as React.ComponentType<any>;

  const progress = totalHours ? workedHours / totalHours : 0;
  const radius = 84;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  if (isLoading) {
    return (
      <div className="w-full mb-3">
        <div className="flex flex-col xl:flex-row gap-3">
          <div className="flex-1 bg-white p-4 xl:p-5 rounded-lg shadow-lg space-y-3">
            <div className="w-32 h-32 xl:w-36 xl:h-36 mx-auto rounded-2xl bg-gray-200 animate-pulse" />
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded-full animate-pulse w-3/4 mx-auto" />
              <div className="h-2.5 bg-gray-100 rounded-full animate-pulse w-1/2 mx-auto" />
            </div>
          </div>
          <div className="flex-1 bg-white p-4 xl:p-5 rounded-lg shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="h-3 bg-gray-200 rounded-full animate-pulse w-24" />
              <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-7 bg-gray-200 rounded-full animate-pulse w-20" />
            <div className="h-[140px] xl:h-[180px] bg-gray-100 rounded-lg animate-pulse" />
          </div>
          <div className="flex-1 bg-white p-4 xl:p-5 rounded-lg shadow-lg space-y-3">
            <div className="h-3 bg-gray-200 rounded-full animate-pulse w-24" />
            <div className="w-[170px] h-[170px] mx-auto bg-gray-100 rounded-full animate-pulse" />
            <div className="space-y-2 w-full">
              <div className="h-2.5 bg-gray-100 rounded-full animate-pulse w-3/4" />
              <div className="h-2.5 bg-gray-100 rounded-full animate-pulse w-2/3" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mb-3">
      <div className="flex flex-col xl:flex-row gap-3">
        {/* USER CARD */}
        <div className="bg-white p-4 xl:p-5 rounded-lg shadow-lg">
          <div className="relative w-40 h-40 xl:w-44 xl:h-44 mx-auto mb-3 rounded-2xl overflow-hidden shadow-md">
            <img
              src={user?.avatar || DefaultProfile}
              className="w-full h-full object-cover"
              alt="avatar"
            />

            <div
              className="absolute bottom-0 w-full text-white py-2 px-3"
              style={{
                background: 'linear-gradient(to top, #7519B2, transparent)',
              }}
            >
              <h2 className="text-[11px] font-semibold truncate">{user?.name}</h2>
              <p className="text-[10px] text-white/70 truncate">{user?.designation}</p>
            </div>
          </div>

          <div className="space-y-1 text-[11px]">
            <p className="text-[#625F6B] truncate">{user?.mobile}</p>
            <p className="text-[#625F6B] truncate">{user?.email}</p>
            <p className="text-[9px] text-[#9E9E9E80] mt-2 truncate">
              {localTime} local time
            </p>
          </div>
        </div>

        {/* CHART CARD */}
        <div className="flex-1 bg-white p-4 xl:p-5 rounded-lg shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[#B9A5F0] font-medium text-xs">Weekly Hours</h3>
            <img src={ProgressIcon} className="h-4 w-4" />
          </div>

          <div className="mb-2 flex items-baseline gap-2">
            <p className="text-lg font-semibold">{workedHours}h</p>
            <p className="text-[10px] text-gray-400">Total Work Time</p>
          </div>

          <div className="h-[140px] xl:h-[180px] mb-2">
            <ResponsiveContainerComponent width="100%" height="100%">
              <BarChart data={chartData} barGap={2} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: '#6B7280' }}
                  angle={-20}
                  textAnchor="end"
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  formatter={(v: any) => (v ? `${v}h` : '')}
                  contentStyle={{ fontSize: 11, borderRadius: 10 }}
                />
                <Bar dataKey="value" fill="#6E3EF5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainerComponent>
          </div>

          <p className="text-[10px] text-[#625F6B]">Last 7 Days Work Hours</p>
        </div>

        <div className="flex-1 bg-white p-4 xl:p-5 rounded-lg shadow-lg flex flex-col items-center">
          <h3 className="text-[#B9A5F0] text-xs font-semibold mb-3 w-full">
            HRMS Punching
          </h3>

          <div className="relative w-[170px] h-[170px]">
            <svg className="w-full h-full" viewBox="0 0 300 300">
              <defs>
                <linearGradient
                  id="progressGradient2"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#6E3EF5" />
                  <stop offset="100%" stopColor="#EFE9FF" />
                </linearGradient>
              </defs>
              <circle
                cx="150"
                cy="150"
                r={120}
                stroke="#D4D4D4"
                strokeWidth="10"
                fill="none"
              />
              <circle
                cx="150"
                cy="150"
                r={120}
                stroke="url(#progressGradient2)"
                strokeWidth="10"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                transform="rotate(-90 150 150)"
                style={{ transition: 'stroke-dashoffset 1s ease' }}
              />
            </svg>

            <div className="absolute inset-0 flex justify-center items-center">
              {[0, 90, 180, 270].map((deg) => {
                const distance = deg === 180 ? -90 : -100;
                return (
                  <div
                    key={deg}
                    className="absolute"
                    style={{
                      transform: `rotate(${deg}deg) translateY(${distance}px)`,
                      transformOrigin: 'center',
                    }}
                  >
                    <div className="w-[4px] h-5 bg-[#6E3EF5]" />
                  </div>
                );
              })}
            </div>

            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-2xl text-[#000000]">{hrmsPunchingData?.loggedInTime}</p>
              <p className="text-[#8F8F8F80] text-[9px]">Logged in time</p>
              <p className="text-[#8F8F8F80] text-[9px]">HRMS Desktop App</p>
            </div>
          </div>

          <div className="mt-2 space-y-1.5 w-full">
            <p className="text-[#3ABE59] font-semibold text-[11px]">
              Start:{' '}
              <span className="text-[#625F6B] text-[11px]">
                {hrmsPunchingData?.startTime || '--'}
              </span>
            </p>
            <p className="text-[#D9786B] font-semibold text-[11px]">
              End:{' '}
              <span className="text-[#625F6B] text-[11px]">
                {hrmsPunchingData?.endTime || '--'}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeTaskTracker;
